import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import '../App.css';

function AlertHistory() {
  const { darkMode } = useTheme();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0 });

  useEffect(() => {
    fetchAlerts();
  }, [statusFilter, pagination.offset]);

  const fetchAlerts = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const statusParam = statusFilter ? `&status=${statusFilter}` : '';
      const response = await fetch(
        `/api/dashboard/alert-history?limit=${pagination.limit}&offset=${pagination.offset}${statusParam}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch alert history');
      }

      const data = await response.json();
      setAlerts(data.alerts);
      setPagination(prev => ({ ...prev, total: data.total }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      sent: { background: '#dcfce7', color: '#166534', border: '1px solid #16a34a' },
      failed: { background: '#fee2e2', color: '#991b1b', border: '1px solid #dc2626' },
      queued: { background: '#fef3c7', color: '#92400e', border: '1px solid #f59e0b' },
    };

    const style = styles[status] || styles.queued;

    return (
      <span style={{
        ...style,
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '600',
        textTransform: 'capitalize'
      }}>
        {status}
      </span>
    );
  };

  const getAlertTypeIcon = (type) => {
    switch (type) {
      case 'instant':
        return '⚠️';
      case 'daily_report':
        return '📊';
      case 'test':
        return '🧪';
      default:
        return '📧';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleNextPage = () => {
    if (pagination.offset + pagination.limit < pagination.total) {
      setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }));
    }
  };

  const handlePrevPage = () => {
    if (pagination.offset > 0) {
      setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }));
    }
  };

  return (
    <div className="page-container">
      <div className="content-header" style={{ marginBottom: '20px' }}>
        <h1>Alert History</h1>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPagination(prev => ({ ...prev, offset: 0 }));
            }}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: darkMode ? '1px solid #374151' : '1px solid #d1d5db',
              background: darkMode ? '#1f2937' : 'white',
              color: darkMode ? '#fff' : '#111',
              cursor: 'pointer'
            }}
          >
            <option value="">All Statuses</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
            <option value="queued">Queued</option>
          </select>

          <button onClick={fetchAlerts} className="btn btn-primary">
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading" style={{ padding: '40px', textAlign: 'center' }}>
          Loading alert history...
        </div>
      ) : error ? (
        <div className="card" style={{ padding: '20px', textAlign: 'center', color: '#ef4444' }}>
          Error: {error}
        </div>
      ) : alerts.length === 0 ? (
        <div className="card" style={{ padding: '40px', textAlign: 'center' }}>
          <p style={{ color: darkMode ? '#9ca3af' : '#6b7280', margin: 0 }}>
            No alert history found. Alerts will appear here once email notifications are sent.
          </p>
        </div>
      ) : (
        <>
          <div className="card">
            <div className="card-body" style={{ padding: 0 }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Monitor</th>
                    <th>Recipient</th>
                    <th>Subject</th>
                    <th>Status</th>
                    <th>Sent At</th>
                  </tr>
                </thead>
                <tbody>
                  {alerts.map((alert) => (
                    <tr key={alert.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span>{getAlertTypeIcon(alert.alert_type)}</span>
                          <span style={{ textTransform: 'capitalize' }}>
                            {alert.alert_type.replace('_', ' ')}
                          </span>
                        </div>
                      </td>
                      <td>
                        {alert.config_name ? (
                          <div>
                            <div style={{ fontWeight: '500' }}>{alert.config_name}</div>
                            <div style={{ fontSize: '12px', color: darkMode ? '#9ca3af' : '#6b7280' }}>
                              {alert.object_type}: {alert.object_name}
                            </div>
                          </div>
                        ) : (
                          <span style={{ color: darkMode ? '#9ca3af' : '#6b7280' }}>—</span>
                        )}
                      </td>
                      <td>{alert.recipient_email}</td>
                      <td>
                        <div style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {alert.subject}
                        </div>
                      </td>
                      <td>{getStatusBadge(alert.status)}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatDate(alert.sent_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div style={{
            marginTop: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ color: darkMode ? '#9ca3af' : '#6b7280' }}>
              Showing {pagination.offset + 1} - {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handlePrevPage}
                disabled={pagination.offset === 0}
                className="btn"
                style={{
                  opacity: pagination.offset === 0 ? 0.5 : 1,
                  cursor: pagination.offset === 0 ? 'not-allowed' : 'pointer'
                }}
              >
                Previous
              </button>
              <button
                onClick={handleNextPage}
                disabled={pagination.offset + pagination.limit >= pagination.total}
                className="btn"
                style={{
                  opacity: pagination.offset + pagination.limit >= pagination.total ? 0.5 : 1,
                  cursor: pagination.offset + pagination.limit >= pagination.total ? 'not-allowed' : 'pointer'
                }}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default AlertHistory;
